// contexts/ThemeContext.js - ReactJS Web Version (FIXED)
import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { lightColors, darkColors } from '../constants/colors';

// Theme Types
export const ThemeTypes = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Create Context
const ThemeContext = createContext();

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Get system preference from browser
  const getSystemTheme = useCallback(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return ThemeTypes.DARK;
    }
    return ThemeTypes.LIGHT;
  }, []);

  // Load saved theme preference from localStorage
  const getSavedTheme = useCallback(() => {
    try {
      const savedTheme = localStorage.getItem('theme_preference');
      if (savedTheme && Object.values(ThemeTypes).includes(savedTheme)) {
        return savedTheme;
      }
    } catch (error) {
      console.error('Error loading theme preference:', error);
    }
    return ThemeTypes.SYSTEM;
  }, []);

  const [themeType, setThemeType] = useState(ThemeTypes.SYSTEM);
  const [systemTheme, setSystemThemeState] = useState(getSystemTheme());

  // Load saved theme on mount
  useEffect(() => {
    const saved = getSavedTheme();
    setThemeType(saved);
  }, [getSavedTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleThemeChange = (e) => {
      setSystemThemeState(e.matches ? ThemeTypes.DARK : ThemeTypes.LIGHT);
    };

    darkModeMediaQuery.addEventListener('change', handleThemeChange);
    return () => darkModeMediaQuery.removeEventListener('change', handleThemeChange);
  }, []);

  // Save theme preference when it changes
  useEffect(() => {
    try {
      localStorage.setItem('theme_preference', themeType);
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  }, [themeType]);

  // Calculate effective theme (actual theme being applied)
  const effectiveTheme = useMemo(() => {
    if (themeType === ThemeTypes.SYSTEM) {
      return systemTheme || ThemeTypes.LIGHT;
    }
    return themeType;
  }, [themeType, systemTheme]);

  // Get current colors based on effective theme
  const colors = useMemo(() => {
    return effectiveTheme === ThemeTypes.DARK ? darkColors : lightColors;
  }, [effectiveTheme]);

  const isDark = effectiveTheme === ThemeTypes.DARK;

  // Theme management functions
  const setLightTheme = useCallback(() => {
    setThemeType(ThemeTypes.LIGHT);
  }, []);

  const setDarkTheme = useCallback(() => {
    setThemeType(ThemeTypes.DARK);
  }, []);

  // Renamed to avoid conflict with state setter
  const setSystemTheme = useCallback(() => {
    setThemeType(ThemeTypes.SYSTEM);
  }, []);

  const toggleTheme = useCallback(() => {
    if (themeType === ThemeTypes.DARK) {
      setThemeType(ThemeTypes.LIGHT);
    } else if (themeType === ThemeTypes.LIGHT) {
      setThemeType(ThemeTypes.DARK);
    } else {
      // If system, toggle based on current effective theme
      setThemeType(isDark ? ThemeTypes.LIGHT : ThemeTypes.DARK);
    }
  }, [themeType, isDark]);

  // Get color helper
  const getColor = useCallback((colorKey) => {
    return colors[colorKey] || colors.primary;
  }, [colors]);

  // Get theme-aware styles
  const createStyles = useCallback((styleCreator) => {
    return styleCreator(colors, isDark);
  }, [colors, isDark]);

  // Apply theme to document body (for CSS variables)
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      root.style.backgroundColor = darkColors.background || '#121212';
      root.style.color = darkColors.textPrimary || '#FFFFFF';
    } else {
      root.removeAttribute('data-theme');
      root.style.backgroundColor = lightColors.background || '#F5F5F5';
      root.style.color = lightColors.textPrimary || '#1A1A1A';
    }
  }, [isDark]);

  const value = {
    // Colors
    colors,
    lightColors,
    darkColors,
    
    // Theme state
    themeType,
    isDark,
    currentTheme: effectiveTheme,
    systemTheme,
    
    // Theme actions
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    toggleTheme,
    
    // Utilities
    getColor,
    createStyles,
    isThemeType: (type) => themeType === type,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom Hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Higher-Order Component
export const withTheme = (WrappedComponent) => {
  return function WithThemeWrapper(props) {
    const theme = useTheme();
    return <WrappedComponent {...props} theme={theme} />;
  };
};

// CSS Variables helper - allows using theme colors in CSS
export const getThemeCSSVariables = (colors) => {
  return {
    '--color-primary': colors.primary,
    '--color-secondary': colors.secondary,
    '--color-background': colors.background,
    '--color-text-primary': colors.textPrimary,
    '--color-text-secondary': colors.textSecondary,
    '--color-text-tertiary': colors.textTertiary,
    '--color-gray-50': colors.gray50,
    '--color-gray-100': colors.gray100,
    '--color-gray-200': colors.gray200,
    '--color-gray-300': colors.gray300,
    '--color-gray-400': colors.gray400,
    '--color-gray-500': colors.gray500,
    '--color-gray-600': colors.gray600,
    '--color-success': colors.success,
    '--color-error': colors.error,
    '--color-warning': colors.warning,
    '--color-accent-gold': colors.accentGold,
  };
};

// Apply theme CSS variables to document root
export const applyThemeCSSVariables = (colors) => {
  const variables = getThemeCSSVariables(colors);
  const root = document.documentElement;
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

// Hook to get theme-aware CSS variables
export const useThemeCSSVariables = () => {
  const { colors } = useTheme();
  return getThemeCSSVariables(colors);
};

// CSS class helper for theme-based classes
export const themeClass = (baseClass, isDark) => {
  return `${baseClass} ${isDark ? `${baseClass}-dark` : `${baseClass}-light`}`;
};

// Example usage in CSS:
/*
:root {
  --color-primary: #e74c3c;
  --color-background: #f5f5f5;
  --color-text-primary: #1a1a2e;
}

[data-theme="dark"] {
  --color-primary: #e74c3c;
  --color-background: #121212;
  --color-text-primary: #ffffff;
}

.my-component {
  background-color: var(--color-background);
  color: var(--color-text-primary);
}
*/